const isSignedUrl = url => (/([?&](X-Amz-Signature|Signature)=|[?&]AWSAccessKeyId=)/i).test(url)
const isLocalImageUrl = url => (/^(blob:|data:)/).test(url)
const getImageUrl = url => isLocalImageUrl(url) || isSignedUrl(url)
  ? url
  : `${url}${!~url.indexOf('?')? '?': '&'}t=${Date.now()}`

const createImage = url =>
  new Promise((resolve, reject) => {
    const image = new Image()
    image.setAttribute('crossOrigin', 'anonymous') // needed to avoid cross-origin issues on CodeSandbox
    image.setAttribute('cacheControl', 'no-cache, invalidate') // needed to avoid cross-origin issues on CodeSandbox
    image.addEventListener('load', () => resolve(image))
    image.addEventListener('error', error => reject(error))
    image.src = getImageUrl(url)
  })

function getRadianAngle(degreeValue) {
  return (degreeValue * Math.PI) / 180
}

const getNormalizedRotation = rotation => ((rotation % 360) + 360) % 360

const createFileFromBlob = (blob, fileName, mimeType) => {
  if (typeof File !== 'undefined') {
    return new File([blob], fileName, { type: mimeType })
  }

  blob.name = fileName
  return blob
}

const fetchImageBlob = async imageSrc => {
  const response = await fetch(getImageUrl(imageSrc))
  if (!response.ok) {
    throw new Error('Failed to fetch image source')
  }

  return response.blob()
}

const drawSourceToCanvas = (source, mimeType) => {
  const width = source.naturalWidth || source.width
  const height = source.naturalHeight || source.height
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')

  canvas.width = width
  canvas.height = height

  if (mimeType === 'image/jpeg') {
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
  }

  ctx.drawImage(source, 0, 0, width, height)

  return canvas
}

const createCanvasFromImage = async(imageSrc, mimeType) => {
  const image = await createImage(imageSrc)
  return drawSourceToCanvas(image, mimeType)
}

const createDisplayOrientedCanvas = async(imageSrc, mimeType) => {
  if (mimeType !== 'image/jpeg') {
    return createCanvasFromImage(imageSrc, mimeType)
  }

  if (typeof window === 'undefined' || !window.createImageBitmap) {
    return createCanvasFromImage(imageSrc, mimeType)
  }

  try {
    const blob = await fetchImageBlob(imageSrc)
    const bitmap = await window.createImageBitmap(blob, { imageOrientation: 'from-image' })

    try {
      return drawSourceToCanvas(bitmap, mimeType)
    } finally {
      if (bitmap.close) {
        bitmap.close()
      }
    }
  } catch (error) {
    console.warn('Falling back to image element for rotation source:', error)
    return createCanvasFromImage(imageSrc, mimeType)
  }
}

// Check browser memory capabilities to prevent Out of Memory
const getBrowserMemoryLimit = () => {
  const testCanvas = document.createElement('canvas')
  const ctx = testCanvas.getContext('2d')
  
  // Test with increasingly large canvas sizes
  const sizes = [1024, 2048, 4096, 8192]
  let maxSize = 1024
  
  for (const size of sizes) {
    try {
      testCanvas.width = size
      testCanvas.height = size
      ctx.createImageData(size, size) // This will throw if too large
      maxSize = size
    } catch (e) {
      break
    }
  }
  
  return maxSize
}

/**
 * This function was adapted from the one in the ReadMe of https://github.com/DominicTobias/react-image-crop
 * Enhanced to prevent Out of Memory errors without compressing the original image
 * @param {string} imageSrc - Image source URL
 * @param {Object} pixelCrop - pixelCrop Object provided by react-easy-crop
 * @param {number} rotation - optional rotation parameter
 * @param {number} quality - JPEG quality (0.1 to 1.0)
 * @param {string} mimeType - Output MIME type (default: 'image/jpeg')
 */
export const getCroppedImg = async(imageSrc, pixelCrop, rotation = 0, quality = 0.9, mimeType = 'image/jpeg') => {
  try {
    const image = await createImage(imageSrc)
    
    // Get browser memory capabilities to prevent Out of Memory
    const maxSafeCanvasSize = getBrowserMemoryLimit()
    
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    // Check if crop area is too large for browser memory
    const cropArea = pixelCrop.width * pixelCrop.height
    const maxSafeArea = maxSafeCanvasSize * maxSafeCanvasSize
    
    let finalWidth = pixelCrop.width
    let finalHeight = pixelCrop.height
    
    // Only scale down if absolutely necessary to prevent Out of Memory
    if (cropArea > maxSafeArea) {
      const scale = Math.sqrt(maxSafeArea / cropArea)
      finalWidth = Math.floor(pixelCrop.width * scale)
      finalHeight = Math.floor(pixelCrop.height * scale)
      console.log(`Scaling down by ${(scale * 100).toFixed(1)}% to prevent Out of Memory`)
    }

    // Set canvas size
    canvas.width = finalWidth
    canvas.height = finalHeight

    // Apply rotation if needed
    if (rotation !== 0) {
      ctx.translate(canvas.width / 2, canvas.height / 2)
      ctx.rotate(getRadianAngle(rotation))
      ctx.translate(-canvas.width / 2, -canvas.height / 2)
    }

    // Draw the cropped area directly - no compression of source image
    ctx.drawImage(
      image,
      pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, // source (original size)
      0, 0, finalWidth, finalHeight // destination (scaled only if needed for memory)
    )

    // Return as blob with specified quality
    // PNG doesn't use the quality parameter
    const blobArgs = mimeType === 'image/png' ? [mimeType] : [mimeType, quality]
    return new Promise((resolve, reject) => {
      canvas.toBlob(file => {
        if (file) {
          resolve(file)
        } else {
          reject(new Error('Failed to create blob from canvas'))
        }
      }, ...blobArgs)
    })
    
  } catch (error) {
    console.error('Error in getCroppedImg:', error)
    throw new Error('Failed to crop image: ' + error.message)
  }
}

export const getRotatedImg = async(imageSrc, rotation = 0, quality = 0.9, mimeType = 'image/jpeg', fileName = 'image.jpg') => {
  try {
    const sourceCanvas = await createDisplayOrientedCanvas(imageSrc, mimeType)
    const normalizedRotation = getNormalizedRotation(rotation)
    const width = sourceCanvas.width
    const height = sourceCanvas.height
    const isQuarterTurn = normalizedRotation === 90 || normalizedRotation === 270

    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    canvas.width = isQuarterTurn ? height : width
    canvas.height = isQuarterTurn ? width : height

    if (mimeType === 'image/jpeg') {
      ctx.fillStyle = '#fff'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
    }

    ctx.translate(canvas.width / 2, canvas.height / 2)
    ctx.rotate(getRadianAngle(normalizedRotation))
    ctx.drawImage(sourceCanvas, -width / 2, -height / 2, width, height)

    const blobArgs = mimeType === 'image/png' ? [mimeType] : [mimeType, quality]
    return new Promise((resolve, reject) => {
      canvas.toBlob(blob => {
        if (blob) {
          resolve(createFileFromBlob(blob, fileName, mimeType))
        } else {
          reject(new Error('Failed to create blob from canvas'))
        }
      }, ...blobArgs)
    })
  } catch (error) {
    console.error('Error in getRotatedImg:', error)
    throw new Error('Failed to rotate image: ' + error.message)
  }
}
